import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/types';
import { AtmosphericStage, StageCard, JuicyButton, CandyIconButton } from '../components/primitives';
import { Star, Trophy, Sparkles, Home } from 'lucide-react-native';

type Props = StackScreenProps<RootStackParamList, 'FreestyleSummary'>;

export const FreestyleSummaryScreen: React.FC<Props> = ({ navigation, route }) => {
    const { questionsCount, starsEarned, subject, yearGroup } = route.params;

    const yearLabel = yearGroup.toLowerCase().startsWith('y')
        ? `Year ${yearGroup.replace(/[^0-9]/g, '')}`
        : yearGroup.charAt(0).toUpperCase() + yearGroup.slice(1);

    return (
        <AtmosphericStage>
            <View style={styles.topBar}>
                <CandyIconButton
                    icon={Home}
                    onPress={() => navigation.navigate('Home')}
                    accessibilityLabel="Return home"
                />
                <View style={{ width: 48 }} />
            </View>

            <ScrollView
                style={styles.scrollArea}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                bounces={true}
            >
                <StageCard style={styles.summaryCard}>
                    {/* Trophy Badge */}
                    <View style={styles.trophyOrb}>
                        <Trophy size={48} color="#D97706" />
                        <View style={styles.sparkleDecoration}>
                            <Sparkles size={24} color="#F59E0B" />
                        </View>
                    </View>

                    <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                        Brilliant Practice!
                    </Text>
                    <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        {yearLabel} • {subject}
                    </Text>

                    {/* Stats Grid */}
                    <View style={styles.statsRow}>
                        <View style={styles.statCard}>
                            <Text style={styles.statNumber} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                {questionsCount}
                            </Text>
                            <Text style={styles.statLabel} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                                Questions Done
                            </Text>
                        </View>

                        <View style={[styles.statCard, styles.statCardGold]}>
                            <View style={styles.starRow}>
                                <Star size={24} color="#F59E0B" fill="#F59E0B" />
                                <Text style={styles.statNumberGold} allowFontScaling={true} maxFontSizeMultiplier={1.4}>
                                    {starsEarned}
                                </Text>
                            </View>
                            <Text style={styles.statLabelGold} allowFontScaling={true} maxFontSizeMultiplier={1.2}>
                                Stars Earned
                            </Text>
                        </View>
                    </View>

                    <Text style={styles.praiseText} allowFontScaling={true} maxFontSizeMultiplier={1.3}>
                        Super work practicing independently! You are growing your skills and confidence every day.
                    </Text>

                    {/* Action Buttons */}
                    <View style={styles.actions}>
                        <JuicyButton
                            label="PRACTICE AGAIN! 🚀"
                            variant="primary"
                            size="large"
                            onPress={() => navigation.replace('FreestyleCurriculum')}
                            testID="practice-again-button"
                        />
                        <View style={{ height: 12 }} />
                        <JuicyButton
                            label="RETURN HOME 🏠"
                            variant="secondary"
                            size="medium"
                            onPress={() => navigation.navigate('Home')}
                            testID="return-home-button"
                        />
                    </View>
                </StageCard>
            </ScrollView>
        </AtmosphericStage>
    );
};

const styles = StyleSheet.create({
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 8,
        zIndex: 10,
    },
    scrollArea: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingBottom: 24,
    },
    summaryCard: {
        width: '100%',
        maxWidth: 560,
        alignItems: 'center',
        paddingVertical: 32,
        paddingHorizontal: 24,
        borderRadius: 36,
    },
    trophyOrb: {
        width: 96,
        height: 96,
        borderRadius: 48,
        backgroundColor: '#FEF3C7',
        borderWidth: 4,
        borderColor: '#FDE68A',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
        position: 'relative',
    },
    sparkleDecoration: {
        position: 'absolute',
        top: -6,
        right: -6,
    },
    title: {
        fontSize: 28,
        fontWeight: '900',
        color: '#1E293B',
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#64748B',
        marginTop: 4,
        marginBottom: 20,
    },
    statsRow: {
        flexDirection: 'row',
        gap: 16,
        width: '100%',
        marginBottom: 20,
    },
    statCard: {
        flex: 1,
        backgroundColor: '#F8FAFC',
        borderWidth: 2,
        borderColor: '#E2E8F0',
        borderRadius: 20,
        paddingVertical: 16,
        alignItems: 'center',
    },
    statNumber: {
        fontSize: 32,
        fontWeight: '900',
        color: '#1E293B',
    },
    statLabel: {
        fontSize: 13,
        fontWeight: '800',
        color: '#64748B',
        marginTop: 4,
    },
    statCardGold: {
        backgroundColor: '#FFFBEB',
        borderColor: '#FDE68A',
    },
    starRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    statNumberGold: {
        fontSize: 32,
        fontWeight: '900',
        color: '#D97706',
    },
    statLabelGold: {
        fontSize: 13,
        fontWeight: '800',
        color: '#B45309',
        marginTop: 4,
    },
    praiseText: {
        fontSize: 15,
        fontWeight: '700',
        color: '#475569',
        textAlign: 'center',
        lineHeight: 22,
        marginBottom: 24,
        paddingHorizontal: 12,
    },
    actions: {
        width: '100%',
        alignItems: 'center',
    },
});
